/**
 * Homepage Metrics Hook
 * 
 * Calculates all Homepage metrics from the global data store
 * Ensures consistency between Homepage and detail screens
 */

import { useMemo } from 'react';
import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import { addDays } from 'date-fns';

export interface TodaysPriorities {
  promisesDueToday: { count: number; value: number };
  casesDueToday: { count: number; value: number };
  unallocatedReceipts: { count: number; value: number };
  disputesRequiringResponse: { count: number; value: number };
}

export interface TodaysWins {
  paymentsReceived: { count: number; value: number; receiptIds: string[] };
  promisesKept: { count: number; promiseIds: string[] };
  casesResolved: { count: number; caseIds: string[] };
  collectionRate: number;
  target: number;
}

export interface KeyMetrics {
  dso: { current: number; target: number };
  cei: { current: number; lastWeek: number };
  promiseSuccessRate: number;
  avgDisputeResolutionDays: number;
}

export interface WeekAheadDay {
  day: string;
  date: string;
  promises: number;
  value: number;
}

export interface HomepageMetrics {
  todaysPriorities: TodaysPriorities;
  todaysWins: TodaysWins;
  keyMetrics: KeyMetrics;
  weekAhead: WeekAheadDay[];
}

/**
 * Calculate Homepage metrics from global data store
 */
export function useHomepageMetrics(): HomepageMetrics {
  return useMemo(() => {
    const store = getGlobalDataStore();
    
    // ============================================
    // TODAY'S PRIORITIES
    // ============================================
    
    // Promises Due Today
    const promisesDueToday = store.getPromisesDueToday();
    const promisesDueTodayValue = promisesDueToday.reduce((sum, p) => sum + p.promisedAmount, 0);
    
    // Cases Due Today
    const casesDueToday = store.getCasesDueToday();
    const casesDueTodayValue = casesDueToday.reduce((sum, c) => sum + c.amount, 0);
    
    // Unallocated Receipts
    const unallocatedReceipts = store.getUnallocatedReceipts();
    const unallocatedReceiptsValue = unallocatedReceipts.reduce((sum, r) => sum + r.unappliedAmount, 0);
    
    // Disputes Requiring Response
    const disputesRequiringResponse = store.getDisputesRequiringResponseToday();
    const disputesRequiringResponseValue = disputesRequiringResponse.reduce((sum, d) => sum + d.amount, 0);
    
    // ============================================
    // TODAY'S WINS
    // ============================================
    
    // Payments Received Today
    const paymentsReceivedToday = store.getReceiptsReceivedToday();
    const paymentsReceivedValue = paymentsReceivedToday.reduce((sum, r) => sum + r.receivedAmount, 0);
    const paymentsReceivedIds = paymentsReceivedToday.map(r => r.receiptNumber);
    
    // Promises Kept Today
    const promisesKeptToday = store.getPromisesKeptToday();
    const promisesKeptIds = promisesKeptToday.map(p => p.id);
    
    // Cases Resolved Today
    const casesResolvedToday = store.getCasesResolvedToday();
    const casesResolvedIds = casesResolvedToday.map(c => c.caseId);
    
    // Collection Rate (calculated from promises)
    const allPromises = store.getAllPromises();
    const keptPromises = allPromises.filter(p => p.status === 'Paid' || p.status === 'Partially Paid');
    const collectionRate = allPromises.length > 0 
      ? Math.round((keptPromises.length / allPromises.length) * 100)
      : 0;
    const target = 85; // Target collection rate
    
    // ============================================
    // KEY METRICS
    // ============================================
    
    // DSO (Days Sales Outstanding) - Mock calculation
    const dso = {
      current: 42,
      target: 40,
    };
    
    // CEI (Collection Effectiveness Index) - Mock calculation
    const cei = {
      current: collectionRate,
      lastWeek: collectionRate - 2, // Simulate improvement
    };
    
    // Promise Success Rate
    const activeAndCompletedPromises = allPromises.filter(
      p => p.status === 'Active' || p.status === 'Paid' || p.status === 'Partially Paid' || p.status === 'Broken'
    );
    const successfulPromises = allPromises.filter(p => p.status === 'Paid' || p.status === 'Partially Paid');
    const promiseSuccessRate = activeAndCompletedPromises.length > 0
      ? Math.round((successfulPromises.length / activeAndCompletedPromises.length) * 100)
      : 0;
    
    // Average Dispute Resolution Days - Mock calculation
    const avgDisputeResolutionDays = 12;
    
    // ============================================
    // WEEK AHEAD
    // ============================================
    
    const weekAhead: WeekAheadDay[] = [];
    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    
    for (let i = 0; i < 5; i++) {
      const date = addDays(new Date(), i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Get promises due on this day
      const promisesDueOnDay = allPromises.filter(p => {
        const promiseDate = new Date(p.promiseDate);
        return promiseDate.toISOString().split('T')[0] === dateStr && p.status === 'Active';
      });
      
      const dayValue = promisesDueOnDay.reduce((sum, p) => sum + p.promisedAmount, 0);
      
      weekAhead.push({
        day: daysOfWeek[i],
        date: dateStr,
        promises: promisesDueOnDay.length,
        value: dayValue,
      });
    }
    
    // ============================================
    // RETURN METRICS
    // ============================================
    
    return {
      todaysPriorities: {
        promisesDueToday: {
          count: promisesDueToday.length,
          value: promisesDueTodayValue,
        },
        casesDueToday: {
          count: casesDueToday.length,
          value: casesDueTodayValue,
        },
        unallocatedReceipts: {
          count: unallocatedReceipts.length,
          value: unallocatedReceiptsValue,
        },
        disputesRequiringResponse: {
          count: disputesRequiringResponse.length,
          value: disputesRequiringResponseValue,
        },
      },
      todaysWins: {
        paymentsReceived: {
          count: paymentsReceivedToday.length,
          value: paymentsReceivedValue,
          receiptIds: paymentsReceivedIds,
        },
        promisesKept: {
          count: promisesKeptToday.length,
          promiseIds: promisesKeptIds,
        },
        casesResolved: {
          count: casesResolvedToday.length,
          caseIds: casesResolvedIds,
        },
        collectionRate,
        target,
      },
      keyMetrics: {
        dso,
        cei,
        promiseSuccessRate,
        avgDisputeResolutionDays,
      },
      weekAhead,
    };
  }, []); // Empty dependency array since data store is singleton
}
